/**
 * Finds the first "Subject: …" line in rendered HTML/plain text and returns the captured subject.
 * Intended for templates that include a line like: Subject: Your stay at Beach House
 */
export function deriveEmailSubjectFromHtml(html) {
  if (!html || typeof html !== 'string') {
    return '';
  }
  if (typeof document === 'undefined') {
    return '';
  }
  const div = document.createElement('div');
  div.innerHTML = html;
  const text = (div.textContent || div.innerText || '')
    .replace(/\r/g, '')
    .replace(/\u00a0/g, ' ');
  const m = text.match(/Subject:\s*([^\n\r]+)/i);
  return m ? m[1].trim() : '';
}

function escapeRegExp(s) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Guest "To" field: templateContext.data may expose GraphQL-shaped primaryEmailAddress
 * or flat appGuest* keys depending on the template definition SQL.
 */
function emailFromPrimaryAddressField(pe) {
  if (pe == null) return '';
  if (typeof pe === 'string') return pe.trim();
  if (typeof pe === 'object' && pe.emailAddress != null) {
    return String(pe.emailAddress).trim();
  }
  return '';
}

/**
 * Reads guest email from flattened templateContext.data and nested shapes
 * (e.g. guestReservation from SQL / Handlebars context).
 */
export function getGuestPrimaryEmailFromTemplateData(data) {
  if (!data || typeof data !== 'object') {
    return '';
  }
  const direct = emailFromPrimaryAddressField(data.primaryEmailAddress);
  if (direct) return direct;

  const nestedGuest = data.guestReservation?.guest?.primaryEmailAddress;
  const fromNested = emailFromPrimaryAddressField(nestedGuest);
  if (fromNested) return fromNested;

  const guestNested = data.guest?.primaryEmailAddress;
  const fromGuest = emailFromPrimaryAddressField(guestNested);
  if (fromGuest) return fromGuest;

  const fallbacks = [
    data.appGuestPrimaryEmailAddress,
    data.appGuestEmail,
  ];
  for (const v of fallbacks) {
    if (v != null && String(v).trim() !== '') {
      return String(v).trim();
    }
  }
  return '';
}

/**
 * Returns subject from the first "Subject: …" line and HTML with that line removed.
 */
export function parseSubjectAndBodyFromEmailHtml(html) {
  if (!html || typeof html !== 'string') {
    return { subject: '', bodyHtml: html || '' };
  }
  if (typeof document === 'undefined') {
    return { subject: '', bodyHtml: html };
  }

  const subject = deriveEmailSubjectFromHtml(html);
  if (!subject) {
    return { subject: '', bodyHtml: html };
  }

  const esc = escapeRegExp(subject);
  const wrapper = document.createElement('div');
  wrapper.innerHTML = html;

  const first = wrapper.firstElementChild;
  if (first) {
    const normalized = (first.textContent || '').replace(/\s+/g, ' ').trim();
    if (new RegExp(`^Subject:\\s*${esc}\\s*$`, 'i').test(normalized)) {
      first.remove();
      return { subject, bodyHtml: wrapper.innerHTML.trim() };
    }
  }

  let bodyHtml = wrapper.innerHTML;
  bodyHtml = bodyHtml.replace(
    new RegExp(`<p[^>]*>\\s*Subject:\\s*${esc}\\s*</p>`, 'gi'),
    ''
  );
  bodyHtml = bodyHtml.replace(
    new RegExp(`<div[^>]*>\\s*Subject:\\s*${esc}\\s*</div>`, 'gi'),
    ''
  );
  bodyHtml = bodyHtml.replace(
    new RegExp(`<h[1-6][^>]*>\\s*Subject:\\s*${esc}\\s*</h[1-6]>`, 'gi'),
    ''
  );
  bodyHtml = bodyHtml.replace(
    new RegExp(`^\\s*Subject:\\s*${esc}\\s*(<br\\s*/?>\\s*)+`, 'im'),
    ''
  );
  bodyHtml = bodyHtml.replace(new RegExp(`Subject:\\s*${esc}\\s*`, 'i'), '');

  return { subject, bodyHtml: bodyHtml.trim() };
}
